import { useState } from "react";
import { Slider, Checkbox, FormControlLabel } from "@mui/material";
import { useCurrency } from "../context/CurrencyContext";
import ColorSwatches from "./ColorSwatches";
import styles from "./ItemFilters.module.css";

// Filter sidebar for the item listing page. Filter values live in the
// parent (ItemListPage) so URL sync and clearing are straightforward.
//
// `facets` describes the ranges/options derived from the current item set
// or returned by the catalog endpoint:
//   { price: {min, max}, volume: {min, max}, colors: [...],
//     intendedUse: [...], frameSystem: [...] }
export default function ItemFilters({ facets, value, onChange, onClear }) {
  const { formatAmount } = useCurrency();
  const [openFrame, setOpenFrame] = useState(false);

  function patch(p) { onChange?.({ ...value, ...p }); }

  const priceRange = value.priceRange ?? [facets.price.min, facets.price.max];
  const volumeRange = value.volumeRange ?? [facets.volume.min, facets.volume.max];

  return (
    <aside className={styles.aside}>
      <div className={styles.heading}>FILTERS</div>

      <Section title="PRICE">
        <div className={styles.rangeLabels}>
          <span>{formatAmount(priceRange[0])}</span>
          <span>{formatAmount(priceRange[1])}</span>
        </div>
        <Slider
          value={priceRange}
          min={facets.price.min}
          max={facets.price.max}
          onChange={(_, v) => patch({ priceRange: v })}
          valueLabelDisplay="off"
          size="small"
        />
      </Section>

      <Section title="VOLUME (LITERS)">
        <div className={styles.rangeLabels}>
          <span>{volumeRange[0]}</span>
          <span>{volumeRange[1]}</span>
        </div>
        <Slider
          value={volumeRange}
          min={facets.volume.min}
          max={facets.volume.max}
          onChange={(_, v) => patch({ volumeRange: v })}
          valueLabelDisplay="off"
          size="small"
        />
      </Section>

      <Section title="COLOR FAMILY" onClear={() => patch({ colors: [] })}>
        <ColorSwatches
          colors={facets.colors}
          size="md"
          selectedId={(value.colors || []).join(",")}
          onSelect={(c) => {
            const set = new Set(value.colors || []);
            if (set.has(c.id)) set.delete(c.id); else set.add(c.id);
            patch({ colors: Array.from(set) });
          }}
        />
      </Section>

      <Section title="INTENDED USE" onClear={() => patch({ intendedUse: [] })}>
        {facets.intendedUse.map((u) => (
          <FormControlLabel
            key={u}
            className={styles.check}
            control={
              <Checkbox
                size="small"
                checked={(value.intendedUse || []).includes(u)}
                onChange={(_, on) => {
                  const set = new Set(value.intendedUse || []);
                  on ? set.add(u) : set.delete(u);
                  patch({ intendedUse: Array.from(set) });
                }}
              />
            }
            label={u}
          />
        ))}
        {facets.intendedUseMore && <a className={styles.more}>+ More</a>}
      </Section>

      <Section
        title="FRAME SYSTEM"
        collapsible
        open={openFrame}
        onToggle={() => setOpenFrame((x) => !x)}
      >
        {openFrame && facets.frameSystem.map((f) => (
          <FormControlLabel
            key={f}
            className={styles.check}
            control={
              <Checkbox
                size="small"
                checked={(value.frameSystem || []).includes(f)}
                onChange={(_, on) => {
                  const set = new Set(value.frameSystem || []);
                  on ? set.add(f) : set.delete(f);
                  patch({ frameSystem: Array.from(set) });
                }}
              />
            }
            label={f}
          />
        ))}
      </Section>

      {onClear && (
        <button type="button" className={styles.clear} onClick={onClear}>Clear all filters</button>
      )}
    </aside>
  );
}

function Section({ title, children, onClear, collapsible, open, onToggle }) {
  return (
    <section className={styles.section}>
      <header className={styles.sectionHeader}>
        <button
          type="button"
          className={styles.sectionTitle}
          onClick={collapsible ? onToggle : undefined}
        >
          <span>{title}</span>
          {collapsible ? <span className={styles.toggle}>{open ? "−" : "+"}</span> : null}
        </button>
        {onClear ? (
          <button type="button" className={styles.x} onClick={onClear} aria-label={`Clear ${title}`}>×</button>
        ) : null}
      </header>
      {(!collapsible || open) && <div className={styles.sectionBody}>{children}</div>}
    </section>
  );
}
